import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { TrackingOrder } from '../stages';

interface CompletedRewardProps {
  order: TrackingOrder;
}

const GOOGLE_REVIEW_URL = 'https://share.google/OGM77XTqEJtOrXDY8';

function useClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return { copied, copy };
}

export default function CompletedReward({ order }: CompletedRewardProps) {
  const [reviewed, setReviewed] = useState(order.reviewed ?? false);
  const [markingReview, setMarkingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const [referralCode, setReferralCode] = useState<string | null>(
    order.referral_code ?? null,
  );
  const [pixKey, setPixKey] = useState(order.referral_pix_key ?? '');
  const [savingReferral, setSavingReferral] = useState(false);
  const [referralError, setReferralError] = useState<string | null>(null);

  const couponClipboard = useClipboard();
  const referralClipboard = useClipboard();

  const markReviewed = async () => {
    setMarkingReview(true);
    setReviewError(null);
    try {
      const { error } = await supabase.functions.invoke('tracking-reward', {
        body: { action: 'mark_reviewed', cpf_cnpj: order.cpf_cnpj },
      });
      if (error) throw error;
      setReviewed(true);
    } catch {
      setReviewError('Não foi possível confirmar agora. Tente novamente.');
    } finally {
      setMarkingReview(false);
    }
  };

  const saveReferral = async () => {
    if (!pixKey.trim()) {
      setReferralError('Digite sua chave Pix para continuar.');
      return;
    }
    setSavingReferral(true);
    setReferralError(null);
    try {
      const { data, error } = await supabase.functions.invoke('tracking-reward', {
        body: {
          action: 'save_referral',
          cpf_cnpj: order.cpf_cnpj,
          referral_pix_key: pixKey.trim(),
        },
      });
      if (error) throw error;
      const code = data?.referral_code;
      if (code) setReferralCode(code);
    } catch {
      setReferralError('Não foi possível salvar sua chave Pix. Tente novamente.');
    } finally {
      setSavingReferral(false);
    }
  };

  const shareText = referralCode
    ? `Oi! Te indico a Home Office Premium, de móveis corporativos de qualidade. Usa meu código ${referralCode} na hora de fechar e ganha um benefício especial!`
    : '';
  const shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <div className="space-y-4">
      {/* Avaliação no Google → destrava cupom */}
      <div className="rounded-2xl border border-[#E8610A]/20 bg-gradient-to-br from-[#E8610A]/5 to-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A] text-white rounded-xl flex-shrink-0">
            <i className="ri-star-line text-xl"></i>
          </div>
          <div className="flex-1">
            {!reviewed ? (
              <>
                <h4 className="font-bold text-gray-900 mb-1">
                  Avalie no Google e ganhe um cupom de desconto
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">
                  Sua avaliação no Google é muito importante pra gente! Depois de
                  avaliar, seu cupom de agradecimento é liberado aqui na hora. Se
                  puder, manda uma foto dos móveis no seu ambiente.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-star-line"></i>
                    Avaliar no Google
                  </a>
                  <button
                    onClick={markReviewed}
                    disabled={markingReview}
                    className="inline-flex items-center justify-center gap-2 border border-[#E8610A] text-[#E8610A] text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#E8610A]/5 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {markingReview ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Confirmando...
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line"></i>
                        Já avaliei, liberar meu cupom
                      </>
                    )}
                  </button>
                </div>
                {reviewError && (
                  <p className="text-sm text-red-600 mt-3">{reviewError}</p>
                )}
              </>
            ) : order.coupon_code ? (
              <>
                <h4 className="font-bold text-gray-900 mb-1">
                  Obrigado pela avaliação! Aqui está seu cupom
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">
                  Use este cupom na sua próxima compra. Agradecemos de coração por
                  confiar na gente!
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-center">
                    <span className="font-mono text-xl font-bold tracking-[0.2em] text-gray-900">
                      {order.coupon_code}
                    </span>
                  </div>
                  <button
                    onClick={() => couponClipboard.copy(order.coupon_code ?? '')}
                    className="inline-flex items-center gap-2 border border-[#E8610A] text-[#E8610A] text-sm font-semibold px-4 py-3 rounded-lg hover:bg-[#E8610A]/5 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i
                      className={couponClipboard.copied ? 'ri-check-line' : 'ri-file-copy-line'}
                    ></i>
                    {couponClipboard.copied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h4 className="font-bold text-gray-900 mb-1">
                  Obrigado pela avaliação!
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Recebemos seu feedback. Em breve entraremos em contato com seu
                  benefício de agradecimento.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Programa de indicação com cadastro de Pix */}
      <div className="rounded-2xl bg-hop-graphite p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A] rounded-xl flex-shrink-0">
            <i className="ri-money-dollar-circle-line text-xl"></i>
          </div>
          <div className="flex-1">
            <h4 className="font-bold mb-1">Indique e ganhe 5% em Pix</h4>
            <p className="text-sm text-white/60 leading-relaxed">
              Conhece alguém que precisa de móveis corporativos? Indique a
              Home Office Premium. Se a indicação fechar negócio, você recebe{' '}
              <strong className="text-white">5% do valor em Pix na hora</strong>.
              Exemplo: um projeto de R$ 10.000 te rende R$ 500.
            </p>

            {!referralCode ? (
              <div className="mt-4">
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Sua chave Pix para receber
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder="CPF, e-mail, celular ou chave aleatória"
                    className="flex-1 border border-white/20 bg-white/5 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/30 transition-all"
                  />
                  <button
                    onClick={saveReferral}
                    disabled={savingReferral}
                    className="inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {savingReferral ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className="ri-key-2-line"></i>
                        Gerar meu código
                      </>
                    )}
                  </button>
                </div>
                {referralError && (
                  <p className="text-sm text-red-300 mt-2">{referralError}</p>
                )}
                <p className="text-xs text-white/40 mt-2">
                  Ao cadastrar, você ganha um código exclusivo pra compartilhar.
                  Quando alguém comprar usando seu código, você recebe os 5% nesta chave.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-center">
                    <span className="font-mono text-xl font-bold tracking-[0.2em] text-white">
                      {referralCode}
                    </span>
                  </div>
                  <button
                    onClick={() => referralClipboard.copy(referralCode)}
                    className="inline-flex items-center gap-2 border border-white/30 text-white text-sm font-semibold px-4 py-3 rounded-lg hover:bg-white/10 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i
                      className={
                        referralClipboard.copied ? 'ri-check-line' : 'ri-file-copy-line'
                      }
                    ></i>
                    {referralClipboard.copied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-white/20 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Compartilhar no WhatsApp
                </a>
                <p className="text-xs text-white/40">
                  Seu Pix cadastrado: <span className="text-white/70">{pixKey}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}